import { useState } from 'react';
import { KeyboardArrowDown } from '@mui/icons-material';
import { alpha, Button, ListItemIcon, ListItemText, Menu, MenuItem, MenuList, MenuProps, styled, Typography, useTheme } from '@mui/material';
import { colors } from '../../../utils/palette';

interface DefaultSelectedItem {
  main: string;
  child?: string;
}

export interface SubmenuBaseProps {
  buttonTitle: string;
  buttonIcon: JSX.Element;
  setSelectedMenu?: React.Dispatch<React.SetStateAction<DefaultSelectedItem>>;
  selectedMenu?: DefaultSelectedItem;
  isAdminOnlyContent?: boolean;
}

export interface SubmenuWithMenuItems extends SubmenuBaseProps {
  menuItems: { icon?: JSX.Element; title: string; link: () => void }[];
  link?: never; // `link` not present if there is `menuItems`
}

export interface SubmenuWithoutMenuItems extends SubmenuBaseProps {
  menuItems?: never; // `menuItems` not present
  link: () => void; // `link` present if there isn't `menuItems`
}

export type SubmenuProps = SubmenuWithMenuItems | SubmenuWithoutMenuItems;

const Submenu: React.FC<SubmenuProps> = ({ menuItems, buttonIcon, buttonTitle, setSelectedMenu, selectedMenu, link }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const theme = useTheme();
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <>
      <CustomButton
        id="customized-button"
        data-testid="customized-button"
        disableElevation
        onClick={(e) => {
          if ((!menuItems || menuItems.length === 0) && link) link();
          handleClick(e);
          if (setSelectedMenu && link) setSelectedMenu({ main: buttonTitle });
        }}
        startIcon={buttonIcon}
        endIcon={menuItems && <KeyboardArrowDown />}
        selected={selectedMenu?.main === buttonTitle}
      >
        <Typography
          variant="body2"
          sx={{
            color: selectedMenu?.main === buttonTitle ? theme.palette.primary.main : 'inherit',
          }}
        >
          {buttonTitle}
        </Typography>
      </CustomButton>
      {menuItems && menuItems.length > 0 && (
        <StyledMenu
          id="customized-menu"
          MenuListProps={{
            'aria-labelledby': 'customized-button',
          }}
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
        >
          <MenuList data-testid="menu-list">
            {menuItems?.map((menuItem) => {
              return (
                <MenuItem
                  key={menuItem.title}
                  onClick={() => {
                    menuItem.link();
                    handleClose();
                    if (setSelectedMenu) setSelectedMenu({ main: buttonTitle });
                  }}
                >
                  <ListItemIcon>{menuItem.icon}</ListItemIcon>
                  <ListItemText>
                    <Typography variant="body2">{menuItem.title}</Typography>
                  </ListItemText>
                </MenuItem>
              );
            })}
          </MenuList>
        </StyledMenu>
      )}
    </>
  );
};

export default Submenu;

const CustomButton = styled(Button)<{ selected?: boolean }>(({ selected, theme }) => ({
  backgroundColor: selected ? colors.lightSecondary : theme.palette.background.default,
  color: theme.palette.text.secondary,
  marginLeft: theme.spacing(1.25),
  padding: theme.spacing(0.75, 1),
  textTransform: 'none',
  '& svg': {
    color: selected ? theme.palette.primary.main : theme.palette.text.secondary,
  },
  [theme.breakpoints.down('xl')]: {
    marginLeft: theme.spacing(0.25),
  },
}));

export const StyledMenu = styled((props: MenuProps) => (
  <Menu
    elevation={2}
    anchorOrigin={{
      vertical: 'bottom',
      horizontal: 'center',
    }}
    transformOrigin={{
      vertical: 'top',
      horizontal: 'center',
    }}
    {...props}
  />
))(({ theme }) => ({
  '& .MuiPaper-root': {
    borderRadius: 6,
    marginTop: theme.spacing(1),
    minWidth: 270,
    color: theme.palette.text.secondary,
    '& svg': {
      color: theme.palette.text.secondary,
    },
    '& .MuiMenu-list': {
      padding: '4px 0',
    },
    '& .MuiMenuItem-root': {
      '& .MuiSvgIcon-root': {
        fontSize: 18,
        color: theme.palette.text.secondary,
        marginRight: theme.spacing(1.5),
      },
      '&:active': {
        backgroundColor: alpha(theme.palette.primary.main, theme.palette.action.selectedOpacity),
      },
    },
    ...theme.applyStyles('dark', {
      color: theme.palette.grey[300],
    }),
  },
}));
